import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

// Public VAPID key — safe to ship in frontend code, this is what proves to
// the browser's push service that a subscription belongs to this app.
// Generated once for the whole project; never regenerate this without also
// regenerating the matching private key stored as a Supabase Edge Function
// secret (VAPID_PRIVATE_KEY) — they're a matched pair.
const VAPID_PUBLIC_KEY = 'BEBG8LY54SBI1tF0y6j17_UfpLpYhzbUESl1Nr3U6q0y1FNcq5RXR2NeOXvYkysmn4hkrCs9zjr1Va81XNDl3Zw';

// Minimum time between re-prompting a user who hasn't decided yet
// (Notification.permission === 'default'). Stored per-user in localStorage.
const REPROMPT_INTERVAL_MS = 24 * 60 * 60 * 1000; // once a day

// How many times to retry pushManager.subscribe() if it hits the Android
// "no active Service Worker" timing race, and how long to wait between
// attempts. Two retries (three attempts total) with a short pause is
// normally enough — the underlying issue is a brief internal-catch-up
// window on the browser's side, not a real failure.
const SUBSCRIBE_RETRY_ATTEMPTS = 4;
const SUBSCRIBE_RETRY_DELAY_MS = 1500;

// A name for the cross-tab lock below — just an identifier, doesn't need to
// mean anything beyond being unique to this app.
const PUSH_SUBSCRIBE_LOCK_NAME = 'septa-push-subscribe-lock';

// Boilerplate every Web Push implementation needs: the browser's
// subscribe() call wants the VAPID public key as raw bytes, not the
// base64-ish string form it's normally shared in.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// On some Android Chrome versions, right after a brand-new service worker
// is installed (e.g. right after a storage clear + fresh login),
// registration.ready can resolve as "active" a moment before the browser's
// internal push machinery has actually caught up — so the very first
// subscribe() call throws AbortError ("no active Service Worker") even
// though everything looks correct. A short retry clears this reliably,
// since the gap is on the order of milliseconds, not a real failure.
async function subscribeWithRetry(
  registration: ServiceWorkerRegistration,
  attempt = 1
): Promise<PushSubscription> {
  try {
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  } catch (err) {
    // Log the REAL error every time, not a guess — a previous version of
    // this file hardcoded this log line to always say "(no active Service
    // Worker)" regardless of what actually failed, which hid the true
    // error on every attempt except the last one. Don't reintroduce that.
    const errName = err instanceof DOMException ? err.name : (err as any)?.name;
    const errMessage = err instanceof Error ? err.message : String(err);
    console.log(
      `[Push][DEBUG] subscribe() attempt ${attempt} failed — name: ${errName}, message: ${errMessage}`
    );

    const isAbortError = err instanceof DOMException && err.name === 'AbortError';
    if (isAbortError && attempt <= SUBSCRIBE_RETRY_ATTEMPTS) {
      console.log(
        `[Push][DEBUG] retrying, attempt ${attempt + 1} of ${SUBSCRIBE_RETRY_ATTEMPTS + 1}`
      );
      await sleep(SUBSCRIBE_RETRY_DELAY_MS);
      return subscribeWithRetry(registration, attempt + 1);
    }
    throw err;
  }
}

// Runs `fn` while holding a browser-wide lock, so if the user has this app
// open in more than one tab at the same time, only ONE of those tabs is
// ever actually inside `fn` at once — every other tab waits its turn
// instead of racing in alongside it.
//
// Why this matters here: pushManager.subscribe() is supposed to just hand
// back the existing subscription if one already exists, but if two tabs
// call it at nearly the same moment (e.g. both freshly logged in, both
// checking "do I have a subscription yet?" before either has finished
// creating one), the browser can end up creating two DIFFERENT
// subscriptions instead of one — each with its own endpoint, each getting
// its own row in push_subscriptions. That's how this account ended up with
// 97 rows instead of 1. Serializing the whole subscribe-and-save flow
// through this lock means the second tab will simply see "oh, a
// subscription already exists" once it gets its turn, instead of racing
// to create a second one.
//
// navigator.locks is a standard browser API (Web Locks API), supported on
// all current Chrome/Edge/Android Chrome versions this app targets. If for
// any reason it's unavailable (very old browser), we just run `fn`
// directly — worse-case behavior is identical to before this fix, not a
// regression.
async function withPushLock<T>(fn: () => Promise<T>): Promise<T> {
  if (!('locks' in navigator)) {
    console.log('[Push][DEBUG] navigator.locks not available — running without cross-tab lock');
    return fn();
  }
  return (navigator as any).locks.request(PUSH_SUBSCRIBE_LOCK_NAME, async () => {
    console.log('[Push][DEBUG] acquired cross-tab push lock');
    try {
      return await fn();
    } finally {
      console.log('[Push][DEBUG] releasing cross-tab push lock');
    }
  });
}

async function subscribeAndSave(userId: string) {
  console.log('[Push][DEBUG] subscribeAndSave called for user', userId, 'at', new Date().toISOString());

  await withPushLock(async () => {
    console.log('[Push][DEBUG] calling register(/sw.js)...');
    const reg = await navigator.serviceWorker.register('/sw.js');
    console.log('[Push][DEBUG] register() resolved. state right now:', {
      installing: !!reg.installing,
      waiting: !!reg.waiting,
      active: !!reg.active,
      scope: reg.scope,
    });

    // register() can resolve before the service worker has actually finished
    // installing and become active -- pushManager.subscribe() requires an
    // ACTIVE worker, so using register()'s own return value directly (as
    // before) raced against that and threw "no active Service Worker" on a
    // first-ever registration. `.ready` waits until there genuinely IS an
    // active worker controlling the page before resolving.
    console.log('[Push][DEBUG] waiting on serviceWorker.ready...');
    const registration = await navigator.serviceWorker.ready;
    console.log('[Push][DEBUG] ready resolved. registration.active:', !!registration.active, 'scope:', registration.scope);

    // If this browser is already subscribed, this resolves with the existing
    // subscription instead of creating a new one — safe to call on every load.
    // Now that this whole block runs inside the cross-tab lock, this check
    // and the subscribe() below always run as one uninterrupted step per
    // browser, even with multiple tabs open.
    let subscription = await registration.pushManager.getSubscription();
    console.log('[Push][DEBUG] existing subscription found?', !!subscription);
    if (!subscription) {
      console.log('[Push][DEBUG] calling pushManager.subscribe()...');
      subscription = await subscribeWithRetry(registration);
      console.log('[Push][DEBUG] subscribe() succeeded');
    }

    const { endpoint, keys } = subscription.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } };
    if (!endpoint || !keys?.p256dh || !keys?.auth) return;

    // Upsert on endpoint (unique constraint from the push_subscriptions
    // migration) — the same device re-subscribing updates its existing row
    // instead of erroring or duplicating.
    const { error } = await supabase
      .from('push_subscriptions')
      .upsert(
        { user_id: userId, endpoint, p256dh_key: keys.p256dh, auth_key: keys.auth },
        { onConflict: 'endpoint' }
      );
    if (error) console.error('[Push] failed to save subscription:', error);
  });
}

/**
 * Mounted once, at the top of the app (AppRoutes in App.tsx) — not
 * per-portal — so it covers all 5 portals from one place.
 *
 * On login: prompts for Notification permission right away if never asked,
 * or once/day if the user hasn't decided yet ('default' state). A hard
 * 'denied' is never re-prompted (browsers silently ignore
 * requestPermission() calls after that anyway). Once permission is granted
 * — just now, or already granted from a previous visit — (re)subscribes and
 * saves the subscription to push_subscriptions; this part is safe/cheap to
 * run on every load since getSubscription()/subscribe() are idempotent, and
 * as of this version, also protected against multiple tabs racing each
 * other via withPushLock() above.
 */
export function usePushNotifications() {
  const { user } = useAuth();
  const ranForUser = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    console.log('[Push][DEBUG] effect ran. user.id:', user.id, 'ranForUser.current:', ranForUser.current, 'at', new Date().toISOString());
    // Guard against double-firing — e.g. React StrictMode's dev-mode
    // double-invoke, or a re-render with the same logged-in user — only run
    // once per user id per app session.
    if (ranForUser.current === user.id) {
      console.log('[Push][DEBUG] skipping — already ran for this user id');
      return;
    }

    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      // Browser doesn't support web push at all (older Safari, etc) —
      // nothing to do. Modern iOS Safari also requires the app be added to
      // the home screen first, on top of just supporting the API — that's
      // a separate, still-open UI question (flagging where to tell users).
      return;
    }

    ranForUser.current = user.id;

    (async () => {
      const permission = Notification.permission;
      console.log('[Push][DEBUG] Notification.permission is:', permission);

      if (permission === 'granted') {
        try {
          await subscribeAndSave(user.id);
        } catch (err) {
          console.error('[Push][DEBUG] subscribeAndSave threw:', err);
        }
        return;
      }

      if (permission === 'denied') {
        // user said no — never re-prompt (the browser would silently
        // ignore requestPermission() here anyway, so we don't even try).
        console.log('[Push][DEBUG] permission is denied — not re-prompting');
        return;
      }

      // permission === 'default' — not yet decided. Only (re)prompt once a
      // day per user so this doesn't nag on every login. Both branches
      // below now log explicitly — previously this could go completely
      // silent with no way to tell, from the console alone, whether we
      // skipped due to the once-a-day guard or actually called
      // requestPermission() and just didn't see a visible prompt for it
      // (some Chromium browsers show a small icon in the address bar
      // instead of an interruptive popup — worth checking there too).
      const storageKey = `push_prompt_last_${user.id}`;
      const lastPrompt = Number(localStorage.getItem(storageKey) || 0);
      if (Date.now() - lastPrompt < REPROMPT_INTERVAL_MS) {
        console.log(
          `[Push][DEBUG] already prompted within the last 24h (last prompt: ${new Date(lastPrompt).toISOString()}) — not re-prompting yet`
        );
        return;
      }

      localStorage.setItem(storageKey, String(Date.now()));
      console.log('[Push][DEBUG] permission is default, not recently prompted — calling Notification.requestPermission() now');
      const result = await Notification.requestPermission();
      console.log('[Push][DEBUG] requestPermission() resolved with:', result);
      if (result === 'granted') {
        try {
          await subscribeAndSave(user.id);
        } catch (err) {
          console.error('[Push][DEBUG] subscribeAndSave threw:', err);
        }
      }
    })();
  }, [user?.id]);
}
